// app/src/lib/plaid/linkClient.js — Plaid Link integration.
//
// Security invariants:
//   - Plaid Link is the ONLY place credentials are entered. Raw username/password
//     never touch Kanjo code; Link is hosted by Plaid.
//   - The `link_token` is short-lived, fetched per-attempt via authorized API call.
//   - The `public_token` returned by Link is single-use and only valid for exchange.
//   - We never receive `access_token` client-side — that lives in Secrets Manager.
//   - `account_filters` restrict Link to business-relevant accounts; we still
//     defense-in-depth check returned account types server-side.

import { db } from '../db.js';
import { getAccessToken } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';

const PLAID_LINK_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

// OAuth redirect handoff (see /oauth.html + oauth-resume.js). OAuth banks bounce
// the browser out to the bank and back to the registered redirect_uri, which
// discards the in-memory Link handler. We persist the link_token so the return
// page can resume Link, and it stashes the resulting public_token for the app to
// exchange on next load. Both keys are single-use and hold no secrets.
const LINK_TOKEN_KEY = 'kanjoPlaidLinkToken';
const PUBLIC_TOKEN_KEY = 'kanjoPlaidPublicToken';

/** @type {Promise<void> | null} */
let scriptLoad = null;

function loadPlaidScript() {
  if (window.Plaid) return Promise.resolve();
  if (scriptLoad) return scriptLoad;
  scriptLoad = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = PLAID_LINK_URL;
    s.async = true;
    // No crossOrigin / SRI: Plaid doesn't publish a stable hash, and its CDN
    // serves link-initialize.js WITHOUT CORS headers — so setting
    // crossOrigin='anonymous' forces a CORS-mode load that the browser rejects
    // (net::ERR_FAILED → "failed to load Link script"). A standard no-cors
    // <script> load is the supported way to embed Plaid Link.
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('plaid: failed to load Link script'));
    document.head.appendChild(s);
  });
  return scriptLoad;
}

/**
 * Open Plaid Link for a brand-new institution.
 * @returns {Promise<{ institutionName: string, accountIds: string[] }>}
 */
export async function openLink() {
  await loadPlaidScript();
  const linkToken = await requestLinkToken({ mode: 'create' });
  // Persist for the OAuth return trip (oauth.html resumes Link with it). For a
  // non-OAuth bank the flow finishes here and we clear it in onSuccess/onExit.
  try { localStorage.setItem(LINK_TOKEN_KEY, linkToken); } catch { /* private mode */ }
  return new Promise((resolve, reject) => {
    const handler = window.Plaid.create({
      token: linkToken,
      onSuccess: async (publicToken, metadata) => {
        try {
          const result = await exchange(publicToken);
          // The Lambda response is authoritative — write to Dexie from it, not metadata.
          await persistItem(result);
          try { localStorage.removeItem(LINK_TOKEN_KEY); } catch { /* ignore */ }
          resolve({ institutionName: result.institution.name, accountIds: result.accounts.map((a) => a.accountId) });
        } catch (e) { reject(e); }
      },
      onExit: (err) => {
        try { localStorage.removeItem(LINK_TOKEN_KEY); } catch { /* ignore */ }
        err ? reject(new Error(`plaid: link exit ${err.error_code}`)) : reject(new Error('plaid: user cancelled'));
      },
    });
    handler.open();
  });
}

/**
 * Complete a Plaid OAuth redirect. After an OAuth bank returns the user to
 * /oauth.html, that page resumes Link and stashes the public_token; the app
 * calls this on next load (from runPlaidSync) to exchange it and persist the
 * connection. No-op (returns null) when nothing is pending.
 * @returns {Promise<{ institutionName: string, accountIds: string[] } | null>}
 */
export async function finishPendingOAuth() {
  let publicToken = null;
  try { publicToken = localStorage.getItem(PUBLIC_TOKEN_KEY); } catch { /* ignore */ }
  if (!publicToken) return null;
  // Clear first so a failed exchange can't loop on every boot.
  try { localStorage.removeItem(PUBLIC_TOKEN_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(LINK_TOKEN_KEY); } catch { /* ignore */ }
  const result = await exchange(publicToken);
  await persistItem(result);
  return { institutionName: result.institution.name, accountIds: result.accounts.map((a) => a.accountId) };
}

/** Re-open Link in update mode for an existing item that needs reauthentication. */
export async function openLinkUpdateMode(plaidItemId) {
  await loadPlaidScript();
  const linkToken = await requestLinkToken({ mode: 'update', itemId: plaidItemId });
  return new Promise((resolve, reject) => {
    const handler = window.Plaid.create({
      token: linkToken,
      onSuccess: () => resolve({ relinkedItemId: plaidItemId }),
      onExit: (err) => err ? reject(new Error(`plaid: relink exit ${err.error_code}`)) : reject(new Error('plaid: user cancelled')),
    });
    handler.open();
  });
}

async function requestLinkToken(body) {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('plaid: no access token');
  const res = await fetch(`${cfg.apiBaseUrl}/plaid/link-token`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`plaid: link-token ${res.status}`);
  const { linkToken } = await res.json();
  if (!linkToken) throw new Error('plaid: empty link-token');
  return linkToken;
}

async function exchange(publicToken) {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('plaid: no access token');
  const res = await fetch(`${cfg.apiBaseUrl}/plaid/exchange`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ publicToken }),
  });
  if (!res.ok) throw new Error(`plaid: exchange ${res.status}`);
  return res.json();
}

async function persistItem(result) {
  // Defensive: the Lambda response must NOT contain access_token. If it does, refuse.
  if (Object.prototype.hasOwnProperty.call(result, 'access_token') ||
      Object.prototype.hasOwnProperty.call(result, 'accessToken')) {
    throw new Error('plaid: exchange response contains access_token — refusing to persist');
  }
  await db.plaidItems.put({
    plaidItemId: result.itemId,
    userId: result.userId,
    institutionName: result.institution.name,
    institutionLogo: result.institution.logo,
    status: 'HEALTHY',
    lastSyncedAt: Date.now(),
  });
  await db.plaidAccounts.bulkPut(result.accounts.map((a) => ({
    plaidAccountId: a.accountId,
    userId: result.userId,
    plaidItemId: result.itemId,
    name: a.name,
    type: a.type,
    subtype: a.subtype,
    mask: a.mask,
  })));
}

export async function listInstitutions(userId) {
  return db.plaidItems.where('userId').equals(userId).toArray();
}

export async function disconnectInstitution(plaidItemId) {
  const cfg = getConfig();
  const at = await getAccessToken();
  const res = await fetch(`${cfg.apiBaseUrl}/plaid/disconnect`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ itemId: plaidItemId }),
  });
  if (!res.ok) throw new Error(`plaid: disconnect ${res.status}`);
  await db.transaction('rw', db.plaidItems, db.plaidAccounts, db.plaidTransactions, async () => {
    await db.plaidTransactions.where('plaidItemId').equals(plaidItemId).delete();
    await db.plaidAccounts.where('plaidItemId').equals(plaidItemId).delete();
    await db.plaidItems.delete(plaidItemId);
  });
}
